function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/**
 * Multi metrics menu: clickable list of metrics(events, profile, funnels, retention, formula, cohorts) to go inside mp-drop-menu
 */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { debounce } from 'common/util/lodash-wrapped';
import { TRACKING_EVENTS } from 'common/query-builder/query-block/constants';
import { MetricType } from 'common/types/reports/bookmark';
import { ExpansionToggleItemClickEvent, ItemClickEvent, ItemsMenuSize, SearchMatchResult } from 'common/components/items-menu/types';
import { getProjectId, getWorkspaceId } from 'common/report/util';
import { TagInputChangeAction, TagInputChangeEvent, TagInputSearchMode } from 'common/components/tag-input/types';
import { TagItem } from 'common/components/tag-input/tag-item';
import { SelectTheme } from 'common/components/select/constants';
import { SelectReact } from 'common/components/select';
import { Directions } from 'common/components/drop-menu/types';
import { ItemsMenuReact } from 'common/components/items-menu';
import { ResizablePanelReact } from 'common/components/resizable-panel';
import { Direction, PanelName } from 'common/components/resizable-panel/types';
import { SkeletonListReact } from 'common/components/skeleton-list';
import { SkeletonListRowSize } from 'common/components/skeleton-list/types';
import { TagInputReact } from 'common/components/tag-input';
import { Placement } from 'common/components/tooltip/types';
import { fuzzyMatch } from 'common/util/fuzzy-match';
import { useEventsController } from 'common/contexts/react/events-controller-context';
import { useTranslation } from 'common/i18n/hooks';
import { useCohorts } from 'common/contexts/react/cohorts-context';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useDomEvent } from 'common/hooks/useDomEvent';
import { emitTrackEvent } from 'common/util/dom';
import { spans } from 'common/util/tracing';
import { TrackingUsageContext } from 'common/report/tracking-setup';
import { getNow } from 'common/util/timing';
import { useQueryBuilderContext } from 'common/contexts/react/query-builder-context';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { useChildMenuCache } from '../child-menu';
import { getChildMenuHeight } from '../utils/util';
import { ActionType, RefreshMenuItem } from '../events-menu/types';
import { isEventItem, sectionsFromInput } from '../events-menu/util';
import { isScdProperty } from '../properties-menu/types';
import { isEventMenuSectionItem, isProfileMenuSectionItem, MenuType, MultiMetricsClickEvent, MultiMetricsSectionItem, SidebarCategory, SidebarItem, SidebarItemType } from './types';
import styles from './index.module.styl';
import { createContextPanelEventHandlers, getMultiMetricsItemValue, getTooltipData } from './utils';
import { useCreateCustomSections, useMultiMetricsSections, useSearchSelectedTagItems, useSearchTagMenuSections, useSidebarSections } from './hooks';
import { TrackingEvent, TrackProperties } from './tracking';
interface Props {
  selected: MultiMetricsSectionItem | null;
  dataGroupName: string | null;
  allDataGroups?: boolean;
  dataGroupId?: string | null;
  metricLevelDataGroups?: boolean;
  descriptionEditDisabled?: boolean;
  excludeAutotrackEvents?: boolean;
  excludeCustomEvents?: boolean;
  excludedMetricsRowTypes?: MetricType[];
  hideEditControls?: boolean;
  projectHasCohorts?: boolean;
  projectHasPeople?: boolean;
  readonly?: boolean;
  showAllEvents?: boolean;
  showAnyEvent?: boolean;
  showSessionEvent?: boolean;
  showSpecialDatasetProperty?: boolean;
  showTopEvents?: boolean;
  supportB2bCohorts?: boolean;
  queryOrigin?: `canvas` | `experiment` | `report`;
  trackName?: string;
  userId: number;
  projectId: string;
  workspaceId: string;
  onUpdateInlineEvent: (customEventId: null | number) => Promise<void>;
  onUpdateInlineCohort: (cohortItem: any) => Promise<void>;
  onTrack: (ev: CustomEvent) => void;
  onSelect: (sectionItem: MultiMetricsSectionItem) => void;
  onCreate: (ev: any) => void;
  onDelete: () => void;
}
export function MultiMetricsMenu(props: Props) {
  const menuRef = useRef<HTMLDivElement>();
  const menuBodyRef = useRef<HTMLDivElement>();
  const searchInputRef = useRef<HTMLDivElement>();
  const menuOpenedAtRef = useRef<number>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState<boolean>(false);
  const [isSidebarTagSectionExpanded, setSidebarTagSectionExpanded] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [searchVerified, setSearchVerified] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<SidebarCategory>(SidebarCategory.All);
  const [selectedTagSet, setSelectedTagSet] = useState<Set<string>>(new Set<string>());
  const eventsController = useEventsController();
  const cohortsStore = useCohorts();
  const currentProject = useCurrentProject();
  const behaviors = useBehaviors();
  const metrics = useMetrics();
  const i18n = useTranslation();
  const queryBuilderContext = useQueryBuilderContext();
  useDomEvent(menuRef.current, "track", props.onTrack);
  const {
    t,
    print
  } = i18n;
  const userId = props.userId;
  const projectId = Number(props.projectId || getProjectId());
  const workspaceId = Number(props.workspaceId || getWorkspaceId());
  const dataGroupName = props.dataGroupName;
  const metricLevelDataGroups = props.metricLevelDataGroups;
  const excludedMetricTypes = new Set(props.excludedMetricsRowTypes);
  const isRightAlignedQb = Boolean(queryBuilderContext.isCoreReportQb && queryBuilderContext.qbAlignment === Directions.Right);
  if (menuOpenedAtRef.current === null) {
    menuOpenedAtRef.current = getNow();
  }
  const cache = useChildMenuCache({
    childMenuName: "multi-metrics-menu",
    userId,
    projectId,
    workspaceId
  });
  const searchSelectedTagItems = useSearchSelectedTagItems({
    selectedTagSet
  });
  const searchTagMenuSections = useSearchTagMenuSections({
    selectedTagSet
  });
  const createCustomSections = useCreateCustomSections({
    excludedMetricTypes
  });
  const sidebarSections = useSidebarSections({
    selectedCategory,
    selectedTagSet,
    dataGroupName,
    metricLevelDataGroups,
    excludedMetricTypes,
    isSidebarTagSectionExpanded
  });
  const {
    isLoading,
    sections: multiMetricsSections
  } = useMultiMetricsSections({
    category: selectedCategory,
    dataGroupId: props.dataGroupId || "",
    metricLevelDataGroups,
    excludedMetricTypes,
    excludeAutotrackEvents: props.excludeAutotrackEvents,
    excludeCustomEvents: props.excludeCustomEvents,
    searchFilter,
    searchVerified,
    selectedTagSet,
    selected: props.selected,
    showAllEvents: props.showAllEvents,
    showAnyEvent: props.showAnyEvent,
    showTopEvents: props.showTopEvents,
    showSessionEvent: props.showSessionEvent,
    showSpecialDatasetProperty: props.showSpecialDatasetProperty,
    userId,
    projectId,
    workspaceId,
    i18n
  });
  const eventMenuSpanId = "event-menu";
  const eventMenuLoadingSpanId = "event-menu.loading";
  useEffect(() => {
    spans.getOrCreateSpan({
      id: eventMenuSpanId
    });
    spans.getOrCreateSpan({
      id: eventMenuLoadingSpanId,
      parentId: eventMenuSpanId
    });
    return () => {
      spans.endSpanById(eventMenuSpanId);
    };
  }, []);
  useEffect(() => {
    if (!isLoading) {
      spans.endSpanById(eventMenuLoadingSpanId);
    }
  }, [isLoading]);
  const getTrackingProps = (_ref: {
    selectedSectionItem: MultiMetricsSectionItem;
    labelMatch?: SearchMatchResult;
    subLabelMatch?: SearchMatchResult;
    customMatch?: SearchMatchResult;
  }) => {
    let {
      selectedSectionItem,
      labelMatch,
      subLabelMatch,
      customMatch
    }: {
      selectedSectionItem: MultiMetricsSectionItem;
      labelMatch?: SearchMatchResult;
      subLabelMatch?: SearchMatchResult;
      customMatch?: SearchMatchResult;
    } = _ref;
    let eventsProps = {};
    if (isEventMenuSectionItem(selectedSectionItem) && isEventItem(selectedSectionItem.value)) {
      eventsProps = {
        [TrackProperties.IsCustomEvent]: selectedSectionItem.value.custom,
        [TrackProperties.IsAutoTrackEvent]: selectedSectionItem.value.isCollectEverything,
        [TrackProperties.MetaDataSearchEnabled]: true,
        [TrackProperties.IsVerified]: selectedSectionItem.value.verified
      };
    }
    let profileProps = {};
    if (isProfileMenuSectionItem(selectedSectionItem) && selectedSectionItem.value !== "$all_people" && isScdProperty(selectedSectionItem.value)) {
      profileProps = {
        [TrackProperties.PropertyType]: selectedSectionItem.value.type
      };
    }
    const selectedMetricValue = getMultiMetricsItemValue(selectedSectionItem);
    return _objectSpread(_objectSpread({
      [TrackProperties.SelectedCategory]: selectedCategory,
      [TrackProperties.SelectedMenuType]: selectedSectionItem.menuType,
      [TrackProperties.SelectedMetricName]: selectedSectionItem.label,
      [TrackProperties.SelectedMetricValue]: selectedMetricValue,
      [TrackProperties.IsRecentlyViewed]: selectedSectionItem.isRecentlyUsed,
      [TrackProperties.SearchFilter]: searchFilter,
      [TrackProperties.SearchLabelMatch]: labelMatch,
      [TrackProperties.SearchSubLabelMatch]: subLabelMatch,
      [TrackProperties.SearchCustomMatch]: customMatch,
      [TrackProperties.ElapsedTime]: getNow() - menuOpenedAtRef.current
    }, eventsProps), profileProps);
  };
  const addRecentItem = (menuType: MenuType, sectionItem: MultiMetricsSectionItem): void => {
    cache.addRecentItem(menuType, _objectSpread(_objectSpread({}, sectionItem), {}, {
      label: print(sectionItem.label)
    }), item => "".concat(item.menuType, "-").concat(getMultiMetricsItemValue(item)));
  };
  const deleteTag = (tag: string): void => {
    const updatedSelectedTagSet = new Set(selectedTagSet);
    updatedSelectedTagSet.delete(tag);
    setSelectedTagSet(updatedSelectedTagSet);
  };
  const addTag = (tag: string): void => {
    const updatedSelectedTagSet = new Set(selectedTagSet);
    updatedSelectedTagSet.add(tag);
    setSelectedTagSet(updatedSelectedTagSet);
  };
  // TODO - mixpanel.track
  const track = function track(eventName) {
    let extraProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let {
      cancelable = false
    } = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    const additionalProps = {
      ["usage context"]: TrackingUsageContext.Report,
      ["query block menu"]: "multi-metrics-menu",
      ["query origin"]: props.queryOrigin
    };
    if (props.trackName) {
      additionalProps["tab"] = props.trackName;
    }
    emitTrackEvent(menuRef.current, {
      props: _objectSpread(_objectSpread({}, extraProps), additionalProps),
      eventName,
      cancelable
    });
  };
  const refreshEvents = (): void => {
    eventsController.refreshData();
    track(TRACKING_EVENTS.REFRESHED_EVENTS);
  };
  const handleSelectItem = (sectionItem: MultiMetricsSectionItem): void => {
    addRecentItem(sectionItem.menuType, sectionItem);
    props.onSelect(sectionItem);
  };
  const _handleInlineEvent = async (customEventId: null | number): Promise<void> => {
    setIsModalOpen(true);
    await props.onUpdateInlineEvent(customEventId);
    setIsModalOpen(false);
  };
  const _handleInlineCohort = async (cohortItem): Promise<void> => {
    setIsModalOpen(true);
    await props.onUpdateInlineCohort(cohortItem);
    setIsModalOpen(false);
  };
  const handleCreateInline = function handleCreateInline(menuType: MenuType) {
    let from: `inner` | `outer` = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "outer";
    track(TrackingEvent.OpenCreateInlineMetrics, {
      [TrackProperties.SelectedMenuType]: menuType,
      [TrackProperties.SelectedCategory]: selectedCategory,
      from
    });
    switch (menuType) {
      case MenuType.Event:
        _handleInlineEvent(null);
        break;
      case MenuType.Cohort:
        _handleInlineCohort(null);
        break;
      default:
        props.onCreate({
          detail: {
            item: menuType
          }
        });
        break;
    }
  };
  const handleClickItem = (_ref2: MultiMetricsClickEvent): void => {
    let {
      detail
    }: MultiMetricsClickEvent = _ref2;
    const {
      item
    } = detail;
    if ((item.value as RefreshMenuItem).action === ActionType.Refresh) {
      refreshEvents();
      return;
    }
    track(TrackingEvent.SelectMetric, getTrackingProps({
      selectedSectionItem: item
    }));
    handleSelectItem(item);
  };
  const handleSearchInput = (searchInput: string): void => {
    const isStartingSearch = Boolean(searchInput && !searchFilter);
    const isClearingSearch = Boolean(searchFilter && !searchInput);
    setSearchFilter(searchInput);
    if (isStartingSearch) {
      track(TRACKING_EVENTS.SEARCH_STARTED);
    } else if (isClearingSearch) {
      track(TRACKING_EVENTS.SEARCH_CLEARED);
    }
  };
  const debouncedSearchInput = useCallback(debounce(searchInput => {
    handleSearchInput(searchInput);
  }, 200), [handleSearchInput]);
  const handleSearchInputChange = (ev: TagInputChangeEvent): void => {
    const {
      detail
    } = ev;
    if (detail.action === TagInputChangeAction.Text) {
      debouncedSearchInput(detail.value);
    } else if (detail.action === TagInputChangeAction.RemoveTag) {
      deleteTag(detail.tag.value);
    }
  };
  const handleSearchInputClear = (): void => {
    setSearchFilter("");
    setSelectedTagSet(new Set<string>());
  };
  const handleSearchTagMenuItemClick = (ev: ItemClickEvent<TagItem>): void => {
    addTag(ev.detail.item.value.value);
  };
  const handleClickSidebarItem = (_ref3: ItemClickEvent<SidebarItem>): void => {
    let {
      detail
    }: ItemClickEvent<SidebarItem> = _ref3;
    const itemValue = detail.item.value;
    if (itemValue.type === SidebarItemType.Category) {
      const {
        category
      } = itemValue;
      setSelectedCategory(category);
      if (category === SidebarCategory.Verified) {
        setSearchVerified(true);
      } else {
        // reset verified filter when switching to other categories
        setSearchVerified(false);
      }
      track(TrackingEvent.SelectMetricCategory, {
        [TrackProperties.SelectedCategory]: selectedCategory
      });
    }
    if (itemValue.type === SidebarItemType.EventTag) {
      const {
        tag
      } = itemValue;
      if (searchVerified) {
        setSearchVerified(false);
      }
      if (selectedTagSet.has(tag)) {
        deleteTag(tag);
      } else {
        addTag(tag);
      }
    }
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  const handleClickSidebarExpansionToggleItem = (_ref4: ExpansionToggleItemClickEvent): void => {
    let {
      detail
    }: ExpansionToggleItemClickEvent = _ref4;
    setSidebarTagSectionExpanded(detail.isExpanded);
  };
  const contextPanelEventHandlers = createContextPanelEventHandlers({
    track: function track(eventName: string) {
      let extraProps: Record<string, any> = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      return props.onTrack({
        detail: {
          eventName,
          props: extraProps
        }
      } as CustomEvent);
    },
    handleInlineEvent: customEventId => _handleInlineEvent(customEventId),
    handleInlineCohort: cohort => _handleInlineCohort(cohort),
    additionalArgs: {
      projectId,
      workspaceId
    },
    availableContexts: {
      behaviors,
      metrics
    }
  });
  return <div className={clsx(styles.mpMultiMetricsMenu, "mp-multi-metrics-menu", "improved-menu", "mp-child-menu")} ref={menuRef} style={{
    height: getChildMenuHeight()
  }} data-sentry-component="MultiMetricsMenu" data-sentry-source-file="index.tsx">
      <>
        <div className={styles.searchWrapper}>
          <TagInputReact ref={searchInputRef as React.Ref<any>} className={styles.searchInput} tags={searchSelectedTagItems} placeholder={t("query-builder.multi-metrics-menu.search-placeholder")} minTags={0} menuWidth={180} minInputWidth={150} icon={"search"} showClear searchMode={TagInputSearchMode.Hashtag} mpItemsMenuAttrs={{
          'empty-message': t("query-builder.events-menu.no-tags"),
          'empty-search-results-message': t("query-builder.no-search-results")
        }} controlledMode value={searchFilter} menuSections={searchTagMenuSections} onChange={handleSearchInputChange} onClickMenuItem={handleSearchTagMenuItemClick} onClear={handleSearchInputClear} data-sentry-element="TagInputReact" data-sentry-source-file="index.tsx" />
          <SelectReact className={clsx(styles.createCustomButton, {
          'is-open': isCreateMenuOpen
        })} elref='createInline' label={t("common.create")} icon={{
          leftIcon: "plus",
          rightIcon: ""
        }} menuWidth={150} menuAlignment={Directions.Right} sections={createCustomSections} theme={SelectTheme.Tertiary} tooltip={{
          text: t("query-builder.multi-metrics-menu.create-tooltip"),
          placement: Placement.Top
        }} onChange={ev => handleCreateInline(ev.detail.item.value)} onMenuChange={ev => setIsCreateMenuOpen(ev.detail.isOpen)} data-sentry-element="SelectReact" data-sentry-source-file="index.tsx" />
        </div>

        <div ref={menuBodyRef} className={styles.multiMetricsMenuBody}>
          {/* side navigation */}
          {sidebarSections.length > 0 && <ResizablePanelReact elref='sidebar' className={styles.sidebarPanel} storageKey='multi-metrics-menu-sidebar' defaultSize={130} minSize={130} maxSize={300} disableCollapsing panelName={PanelName.MultiMetrics} defaultAlignment={Direction.Left} containerEl={menuBodyRef.current}>
              {!dataGroupName ? <SkeletonListReact className={clsx(styles.sidebar, "sidebar-skeleton-list")} numRows={10} rowSize={SkeletonListRowSize.Large} /> : <ItemsMenuReact className={styles.sidebar} size={ItemsMenuSize.Small} disableFirstItemPadding sections={sidebarSections} onClickItem={handleClickSidebarItem} onClickExpansionToggleItem={handleClickSidebarExpansionToggleItem} />}
            </ResizablePanelReact>}
          <div className={styles.mpMultiMetricsMenuContent}>
            {[SidebarCategory.Formula, SidebarCategory.Funnel, SidebarCategory.Retention, SidebarCategory.Cohort].includes(selectedCategory) && <ButtonReact className={styles.createMetricButton} theme={ButtonTheme.Tertiary} icon={"plus"} onClick={() => handleCreateInline(selectedCategory === SidebarCategory.Formula ? MenuType.Formula : selectedCategory === SidebarCategory.Funnel ? MenuType.Funnel : selectedCategory === SidebarCategory.Retention ? MenuType.Retention : MenuType.Cohort, "inner")}>
                {selectedCategory === SidebarCategory.Formula ? t("query-builder.multi-metrics-menu.create-formula") : selectedCategory === SidebarCategory.Funnel ? t("query-builder.multi-metrics-menu.create-funnel") : selectedCategory === SidebarCategory.Retention ? t("query-builder.multi-metrics-menu.create-retention") : t("query-builder.multi-metrics-menu.create-cohort")}
              </ButtonReact>}
            {isLoading ? <SkeletonListReact numRows={10} rowSize={SkeletonListRowSize.Large} className={styles.mpMultiMetricsMenuSkeletonList} /> : <ItemsMenuReact className={styles.mpMultiMetricsItemsMenu} elref='mainSelectionMenu' emptyMessage={t("query-builder.no-items")} searchFilter={searchFilter} disableSearchSort disableFirstItemPadding onClick={ev => ev.stopPropagation()} onClickItem={(ev: MultiMetricsClickEvent) => handleClickItem(ev)} onHoverIn={contextPanelEventHandlers.onHoverIn} onHoverOut={contextPanelEventHandlers.onHoverOut} onTooltipContextAction={contextPanelEventHandlers.onTooltipContextAction} onTooltipContextEditDescription={contextPanelEventHandlers.onTooltipContextEditDescription} sections={multiMetricsSections} keydownProxyEl={!isModalOpen && searchInputRef.current} sectionsFromInputFn={(filter, sections) => sectionsFromInput({
            searchFilter: filter,
            sections,
            i18n
          })} customSearchMatcherFn={(item, searchString) => fuzzyMatch(item.searchableContent, searchString)} contextPanelTransform={isCreateMenuOpen && !isRightAlignedQb ? null : (sectionItem: MultiMetricsSectionItem) => {
            var _currentProject$get;
            const tooltipData = getTooltipData(sectionItem, {
              readonly: props.readonly,
              dataGroupName,
              descriptionEditDisabled: props.descriptionEditDisabled,
              hideEditControls: props.hideEditControls,
              eventsController,
              cohortsStore,
              currentProject: (_currentProject$get = currentProject.get()) === null || _currentProject$get === void 0 ? void 0 : _currentProject$get.value,
              behaviors,
              metrics,
              i18n
            });
            if (tooltipData && !tooltipData.entityIcon) {
              tooltipData.entityIcon = sectionItem.icon;
            }
            return tooltipData;
          }} contextPanelPositionParentElement={menuRef.current} />}
          </div>
        </div>
      </>
    </div>;
}